import { useState, type FormEvent } from 'react';
import Reveal from '@/components/base/Reveal';
import { SITE } from '@/config/site';

type Status = 'idle' | 'loading' | 'success' | 'error';

export default function NewsletterSection() {
  const [status, setStatus] = useState<Status>('idle');
  const [message, setMessage] = useState('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formEl = event.currentTarget;
    const fd = new FormData(formEl);

    const honeypot = String(fd.get('website_alt') || '').trim();
    if (honeypot) {
      setStatus('success');
      setMessage('订阅成功，每日热榜摘要会发送到你的邮箱。');
      formEl.reset();
      return;
    }

    fd.delete('website_alt');
    const body = new URLSearchParams(fd as unknown as Record<string, string>).toString();

    try {
      setStatus('loading');
      setMessage('');
      const res = await fetch(SITE.subscribeFormAddr, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body,
      });
      const responseText = await res.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const code = parsed?.code;
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后再试。';

      if (res.ok && code === 'OK') {
        setStatus('success');
        setMessage('订阅成功，每日热榜摘要会发送到你的邮箱。');
        formEl.reset();
      } else {
        setStatus('error');
        setMessage(serverMsg);
      }
    } catch {
      setStatus('error');
      setMessage('网络异常，订阅没有成功，请稍后重试。');
    }
  };

  return (
    <section className="w-full bg-accent-100 py-14 md:py-20">
      <div className="mx-auto w-full max-w-4xl px-4 md:px-6">
        <Reveal>
          <div className="rounded-lg border border-accent-300 bg-background-50 p-6 md:p-10">
            <div className="grid grid-cols-1 gap-8 md:grid-cols-2 md:items-center">
              <div>
                <p className="mb-2 flex items-center gap-2 text-xs font-medium uppercase tracking-[0.2em] text-primary-600">
                  <span className="h-3 w-1 rounded-full bg-primary-500" />
                  Newsletter
                </p>
                <h2 className="text-2xl font-bold leading-snug text-foreground-950">每日热榜摘要</h2>
                <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                  每天早上把正在发酵的话题、观点分布变化和值得一看的评论整理成一封简报，用两分钟看完昨天发生了什么。
                </p>
              </div>

              <form
                id="newsletter-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="flex flex-col gap-3"
                noValidate
              >
                <label htmlFor="newsletter-email" className="text-sm font-medium text-foreground-800">
                  邮箱地址
                </label>
                <input
                  id="newsletter-email"
                  name="email"
                  type="email"
                  required
                  placeholder="you@example.com"
                  className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                />
                <label htmlFor="newsletter-interest" className="text-sm font-medium text-foreground-800">
                  最想关注的方向
                </label>
                <select
                  id="newsletter-interest"
                  name="interest"
                  className="w-full cursor-pointer rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                >
                  <option value="娱乐八卦">娱乐八卦</option>
                  <option value="影视综艺">影视综艺</option>
                  <option value="网络消费">网络消费</option>
                  <option value="科技数码">科技数码</option>
                  <option value="体育赛事">体育赛事</option>
                  <option value="社会观察">社会观察</option>
                </select>

                <input
                  className="form-hp"
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                <button
                  type="submit"
                  disabled={status === 'loading'}
                  className="mt-1 w-full cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {status === 'loading' ? '提交中…' : '订阅每日热榜'}
                </button>

                {status === 'success' && (
                  <p className="flex items-center gap-2 text-sm text-primary-700">
                    <i className="ri-checkbox-circle-line" />
                    {message}
                  </p>
                )}
                {status === 'error' && (
                  <p className="flex items-center gap-2 text-sm text-primary-700">
                    <i className="ri-error-warning-line" />
                    {message}
                  </p>
                )}
              </form>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}